<template>
  <div class="stepper-content" v-if="isLoaded">
    <h3>NW設定(共通)</h3>
    <!-- エンドユーザ名 -->
    <v-row>
      <v-col>
        <app-badge
          item="エンドユーザ名"
          msg="全角、半角英数、記号を含む20文字以内
            ※一部使用出来ない記号がございます。その際はエラーダイアログでご確認ください"
          placement="end"
        />
        <validation-provider
          name="endUserName"
          label="エンドユーザ名"
          v-model="cpaNwSetting.endUserName"
          :rules="{
            required: true,
            max: { length: 20 },
          }"
          v-slot="{ componentField, errorMessage, meta }"
        >
          <app-form-input
            v-bind="componentField"
            :field-meta="meta"
            :error-messages="errorMessage"
          />
        </validation-provider>
      </v-col>
    </v-row>

    <!-- 品目 -->
    <v-row>
      <v-col>
        <app-label-group
          label="品目"
          :value="$filter.enumTo(cpaContract.bandwidthType, 'cpaBandwidthType')"
        />
      </v-col>
    </v-row>

    <!-- 契約帯域 -->
    <v-row v-if="cpaContract.bandwidthType === 'SECURING'">
      <v-col>
        <app-badge item="契約帯域" :isInfo="false" />
        <validation-provider
          label="契約帯域"
          name="bandwidth"
          v-model="cpaNwSetting.bandwidth"
          :rules="{
            required: true,
          }"
          v-slot="{ componentField, errorMessage, meta }"
        >
          <app-select
            item-title="text"
            item-value="value"
            :items="bandwidthOptions"
            v-bind="componentField"
            :error-messages="errorMessage"
            :field-meta="meta"
          />
        </validation-provider>
      </v-col>
    </v-row>
    <v-row v-else>
      <v-col>
        <app-label-group label="契約帯域" value="1Gbps" />
      </v-col>
    </v-row>

    <!-- グローバルアドレス利用 -->
    <v-row>
      <v-col>
        <app-badge
          item="グローバルアドレス利用"
          :isRequired="false"
          :isInfo="false"
        />
        <v-radio-group v-model="cpaNwSetting.isGlobalIpUse" hide-details inline>
          <v-radio label="有" :value="true" />
          <v-radio label="無" :value="false" />
        </v-radio-group>
      </v-col>
    </v-row>

    <!-- 端末割当アドレス(IPv4) -->
    <template v-if="isIpv4">
      <v-row>
        <v-col>
          <app-badge
            item="端末割当アドレス(IPv4)"
            msg="・IPアドレス/サブネットマスク形式(例:192.168.0.0/24)
            ・グローバルアドレス利用が無の場合、プライベートアドレスのみ指定可
            ・サブネットマスクは11~24まで指定可
            ・ネットワークアドレスであること
            ・指定したレンジにNW設定(Act/Sby)のWAN IPアドレス(IPv4)(GW対向ルータ/GWルータ)が含まれないこと
            ・指定したレンジに「172.21.0.0/16」、「172.23.0.0/16」、「172.31.0.0/16」のいずれも含まれないこと
            ・最大30アドレスまで登録可能"
            placement="end"
          />
          <validation-array
            name="ueIpAllocationIpv4List"
            v-slot="{ fields, push, remove }"
            ref="ueIpAllocationIpv4List"
          >
            <template v-for="(item, index) in fields" :key="item.key">
              <v-row class="ga-2">
                <!-- 設定アップロード時に30件以上入れられてしまうのでis_array_maxを実施 -->
                <validation-provider
                  label="端末割当アドレス(IPv4)"
                  :name="`IPv4_${item.key}`"
                  v-model="cpaNwSetting.ueIpAllocationIpv4List![index]"
                  :rules="{
                    required: true,
                    is_ip_subnet: { min: 11, max: 24 },
                    is_private_address: !cpaNwSetting.isGlobalIpUse,
                    is_network_address: true,
                    is_array_max: {
                      list: cpaNwSetting.ueIpAllocationIpv4List,
                      max: 30,
                    },
                    is_not_inclusion_subnet: { list: ipv4list(index) },
                    is_not_specific_cpa_ue_ip_address: true,
                  }"
                  v-slot="{ componentField, errorMessage, meta }"
                >
                  <app-form-input
                    v-bind="componentField"
                    :field-meta="meta"
                    :error-messages="errorMessage"
                    :placeholder="
                      $filter.message('placeholder_ip_address_with_subnet24')
                    "
                  />
                </validation-provider>

                <v-btn
                  icon="$mdiMinus"
                  size="small"
                  variant="outlined"
                  class="rounded-sm ml-2 mr-3"
                  @click="removeUeIpAllocationIpv4List(index, remove)"
                />
              </v-row>
            </template>
            <v-row justify="end" class="mb-4">
              <v-col class="pa-0" v-if="isSmfUpfNote">
                <v-sheet
                  border
                  rounded
                  color="#f8f8f8"
                  class="pa-2 mr-2 text-caption"
                >
                  動的IP割り当て(キャリアサイン)を選択した場合は同時に利用できるアドレス数は半分となります。<br />
                  例：/24を選択の場合、利用できる端末割当アドレス数は127個となります。<br />
                  詳細はCPAサービス説明書をご参照ください。
                </v-sheet>
              </v-col>
              <v-col class="pa-0 d-flex align-center" cols="auto">
                <v-btn
                  icon="$mdiPlus"
                  size="small"
                  variant="outlined"
                  class="rounded-sm ml-2 mr-3"
                  :disabled="cpaNwSetting.ueIpAllocationIpv4List!.length >= 30"
                  @click="addUeIpAllocationIpv4List(push)"
                />
              </v-col>
            </v-row>
          </validation-array>
        </v-col>
      </v-row>
      <v-divider v-if="isSmfUpfNote && isDivider" class="my-4"></v-divider>
    </template>

    <!-- 端末割当アドレス(IPv6) -->
    <template v-if="isIpv6">
      <v-row>
        <v-col>
          <app-badge
            item="端末割当アドレス(IPv6)"
            msg="
              ・IPv6形式(省略不可)(例:2001:0268:f100:0000:0000:0000:0000:0000/48)
              ・サブネットマスクは48固定
              ・ネットワークアドレスであること
              ・指定したレンジにNW設定(Act/Sby)のWAN IPアドレス(IPv6)(GW対向ルータ/GWルータ)が含まれないこと
              ・最大30アドレスまで登録可能"
            placement="end"
          />
          <validation-array
            name="ueIpAllocationIpv6List"
            v-slot="{ fields, push, remove }"
            ref="ueIpAllocationIpv6List"
          >
            <template v-for="(item, index) in fields" :key="item.key">
              <v-row class="ga-2">
                <validation-provider
                  label="端末割当アドレス(IPv6)"
                  :name="`IPv6_${item.key}`"
                  v-model="cpaNwSetting.ueIpAllocationIpv6List![index]"
                  :rules="{
                    required: true,
                    is_ipv6_subnet: { eq: 48 },
                    is_ipv6_not_omit: true,
                    is_ipv6_not_segment: { list: ipv6list(index) },
                    is_array_max: {
                      list: cpaNwSetting.ueIpAllocationIpv6List,
                      max: 30,
                    },
                    is_cpa_v6_network_address: true,
                  }"
                  v-slot="{ componentField, errorMessage, meta }"
                >
                  <app-form-input
                    v-bind="componentField"
                    :field-meta="meta"
                    :error-messages="errorMessage"
                    :placeholder="
                      $filter.message('placeholder_ipv6_address_with_prefix48')
                    "
                  />
                </validation-provider>

                <v-btn
                  icon="$mdiMinus"
                  size="small"
                  variant="outlined"
                  class="rounded-sm ml-2 mr-3"
                  @click="removeUeIpAllocationIpv6List(index, remove)"
                />
              </v-row>
            </template>
            <v-row justify="end">
              <v-col class="pa-0" v-if="isSmfUpfNote">
                <v-sheet
                  border
                  rounded
                  color="#f8f8f8"
                  class="pa-2 mr-2 text-caption"
                >
                  動的IP割り当て(キャリアサイン)を選択した場合は同時に利用できるアドレス数は半分となります。<br />
                  例：/48を選択の場合、利用できるネットワークアドレス数は32,768個となります。<br />
                  詳細はCPAサービス説明書をご参照ください。
                </v-sheet>
              </v-col>
              <v-col class="pa-0 d-flex align-center" cols="auto">
                <v-btn
                  icon="$mdiPlus"
                  size="small"
                  variant="outlined"
                  class="rounded-sm ml-2 mr-3"
                  :disabled="cpaNwSetting.ueIpAllocationIpv6List!.length >= 30"
                  @click="addUeIpAllocationIpv6List(push)"
                />
              </v-col>
            </v-row>
          </validation-array>
        </v-col>
      </v-row>
    </template>

    <h3 class="mt-4">NW設定(Act)</h3>
    <template v-if="isIpv4">
      <v-row>
        <v-col>
          <app-badge
            item="WAN IP アドレス(IPv4)"
            msg="・IPアドレス/サブネットマスク形式(例:192.168.0.1/30)
              ・グローバルアドレス利用が無の場合、プライベートアドレスのみ指定可
              ・サブネットマスクは30固定
              ・ホストアドレスであること
              ・「GWルータ」と「GW対向ルータ」は同じセグメントのアドレスであること
              ・GW対向ルータのWAN IPアドレスは、GWルータのWAN IPアドレスより小さいアドレスであること
              ・NW設定(Sby)のWAN IPアドレス(IPv4)(GW対向ルータ)、WAN IPアドレス(IPv4)(GWルータ)と同じアドレスでないこと
              ・端末割当アドレス(IPv4)で指定したレンジに含まれないこと
              ・「172.31.0.0/16」のレンジに含まれないこと"
            placement="end"
          />
        </v-col>
      </v-row>
      <!-- ACT-GW対向ルータ(IPv4) -->
      <v-row>
        <v-col>
          <small>GW対向ルータ</small>
          <validation-provider
            label="GW対向ルータ"
            name="actWanIpv4Pe"
            v-model="cpaNwSetting.networkIpSettingAct!.wanIpv4Pe"
            :rules="{
              required: true,
              is_ip_subnet: { eq: 30 },
              is_private_address: !cpaNwSetting.isGlobalIpUse,
              is_same_segment: { ip: '@actWanIpv4Gw', targetField: 'GWルータ' },
              is_ip_smaller: { ip: '@actWanIpv4Gw', targetField: 'GWルータ' },
              is_not_network_ip: true,
              is_not_broadcast_ip: true,
              is_act_sby_not_same: { targetList: sbyIpv4list, act_sby: 'Sby' },
              is_not_inclusion_range: {
                targetList: ipv4list(-1),
              },
              is_not_specific_cpa_wan_address: true,
            }"
            v-slot="{ componentField, errorMessage, meta }"
          >
            <app-form-input
              v-bind="componentField"
              :field-meta="meta"
              :error-messages="errorMessage"
            />
          </validation-provider>
        </v-col>
      </v-row>
      <!-- ACT-GWルータ(IPv4) -->
      <v-row>
        <v-col>
          <small>GWルータ</small>
          <validation-provider
            name="actWanIpv4Gw"
            label="GWルータ"
            v-model="cpaNwSetting.networkIpSettingAct!.wanIpv4Gw"
            :rules="{
              required: true,
              is_ip_subnet: { eq: 30 },
              is_private_address: !cpaNwSetting.isGlobalIpUse,
              is_same_segment: {
                ip: '@actWanIpv4Pe',
                targetField: 'GW対向ルータ',
              },
              is_ip_greater: {
                ip: '@actWanIpv4Pe',
                targetField: 'GW対向ルータ',
              },
              is_not_network_ip: true,
              is_not_broadcast_ip: true,
              is_act_sby_not_same: { targetList: sbyIpv4list, act_sby: 'Sby' },
              is_not_inclusion_range: {
                targetList: ipv4list(-1),
              },
              is_not_specific_cpa_wan_address: true,
            }"
            v-slot="{ componentField, errorMessage, meta }"
          >
            <app-form-input
              v-bind="componentField"
              :field-meta="meta"
              :error-messages="errorMessage"
            />
          </validation-provider>
        </v-col>
      </v-row>
    </template>

    <template v-if="isIpv6">
      <v-row>
        <v-col>
          <app-badge
            item="WAN IP アドレス(IPv6)"
            msg="・IPv6形式(省略不可)(例:2001:0268:f100:0090:0000:0000:0000:0001/64)
              ・サブネットマスクは64固定
              ・GW対向ルータの末尾が「.0001」であること
              ・GWルータの末尾が「.0002」であること
              ・NW設定(Sby)のWAN IPアドレス(IPv6)(GW対向ルータ)、WAN IPアドレス(IPv6)(GWルータ)と同じアドレスでないこと
              ・端末割当アドレス(IPv6)で指定したレンジに含まれないこと"
          />
        </v-col>
      </v-row>
      <!-- ACT-GW対向ルータ(IPv6) -->
      <v-row>
        <v-col>
          <small>GW対向ルータ</small>
          <validation-provider
            label="GW対向ルータ"
            name="actWanIpv6Pe"
            v-model="cpaNwSetting.networkIpSettingAct!.wanIpv6Pe"
            :rules="{
              required: true,
              is_ipv6_subnet: { eq: 64 },
              is_ipv6_not_omit: true,
              cpa_ipv6_gw_pe: {
                target: cpaNwSetting.networkIpSettingAct!.wanIpv6Gw,
                pe_gw: 'pe',
              },
              is_ipv6_act_sby_not_same: {
                targetList: sbyIpv6list,
                act_sby: 'Sby',
              },
              is_ipv6_not_inclusion_range: { targetList: ipv6list(-1) },
            }"
            v-slot="{ componentField, errorMessage, meta }"
          >
            <app-form-input
              v-bind="componentField"
              :field-meta="meta"
              :error-messages="errorMessage"
            />
          </validation-provider>
        </v-col>
      </v-row>
      <!-- ACT-GWルータ(IPv6) -->
      <v-row>
        <v-col>
          <small>GWルータ</small>
          <validation-provider
            name="actWanIpv6Gw"
            label="GWルータ"
            v-model="cpaNwSetting.networkIpSettingAct!.wanIpv6Gw"
            :rules="{
              required: true,
              is_ipv6_subnet: { eq: 64 },
              is_ipv6_not_omit: true,
              cpa_ipv6_gw_pe: {
                target: cpaNwSetting.networkIpSettingAct!.wanIpv6Pe,
                pe_gw: 'gw',
              },
              is_ipv6_act_sby_not_same: {
                targetList: sbyIpv6list,
                act_sby: 'Sby',
              },
              is_ipv6_not_inclusion_range: { targetList: ipv6list(-1) },
            }"
            v-slot="{ componentField, errorMessage, meta }"
          >
            <app-form-input
              v-bind="componentField"
              :field-meta="meta"
              :error-messages="errorMessage"
            />
          </validation-provider>
        </v-col>
      </v-row>
    </template>

    <!-- ACT-DNS IPアドレス(IPv4) -->
    <template v-if="isIpv4">
      <v-row>
        <v-col>
          <app-badge
            item="DNS IPアドレス(IPv4)"
            :is-required="false"
            msg="・IPアドレス/サブネットマスク形式(例:192.168.0.1/32)
              ・サブネットマスクは32固定
              ・グローバルアドレス利用が無の場合、プライベートアドレスのみ指定可"
            placement="end"
          ></app-badge>
          <validation-provider
            label="DNS IPアドレス(IPv4)"
            name="actDnsIpv4"
            v-model="cpaNwSetting.networkIpSettingAct!.dnsIpv4"
            :rules="{
              is_ip_subnet: { eq: 32 },
              is_private_address: !cpaNwSetting.isGlobalIpUse,
            }"
            v-slot="{ componentField, errorMessage, meta }"
          >
            <app-form-input
              v-bind="componentField"
              :field-meta="meta"
              :error-messages="errorMessage"
            />
          </validation-provider>
        </v-col>
      </v-row>
    </template>

    <!-- ACT-DNS IPアドレス(IPv6) -->
    <template v-if="isIpv6">
      <v-row>
        <v-col>
          <app-badge
            item="DNS IPアドレス(IPv6)"
            :is-required="false"
            msg="・IPv6形式(省略不可)(例:2001:0268:f100:0090:0000:0000:0000:0001/64)
              ・サブネットマスクは64固定"
          ></app-badge>
          <validation-provider
            name="actDnsIpv6"
            label="DNS IPアドレス(IPv6)"
            v-model="cpaNwSetting.networkIpSettingAct!.dnsIpv6"
            :rules="{
              is_ipv6_subnet: { eq: 64 },
              is_ipv6_not_omit: true,
            }"
            v-slot="{ componentField, errorMessage, meta }"
          >
            <app-form-input
              v-bind="componentField"
              :field-meta="meta"
              :error-messages="errorMessage"
            />
          </validation-provider>
        </v-col>
      </v-row>
    </template>

    <h3 class="mt-4">NW設定(Sby)</h3>
    <template v-if="isIpv4">
      <v-row>
        <v-col>
          <app-badge
            item="WAN IP アドレス(IPv4)"
            msg="・IPアドレス/サブネットマスク形式(例:192.168.0.1/30)
                ・グローバルアドレス利用が無の場合、プライベートアドレスのみ指定可
                ・サブネットマスクは30固定
                ・ホストアドレスであること
                ・「GWルータ」と「GW対向ルータ」は同じセグメントのアドレスであること
                ・GW対向ルータのWAN IPアドレスは、GWルータのWAN IPアドレスより小さいアドレスであること
                ・NW設定(Act)のWAN IPアドレス(IPv4)(GW対向ルータ)、WAN IPアドレス(IPv4)(GWルータ)と同じアドレスでないこと
                ・端末割当アドレス(IPv4)で指定したレンジに含まれないこと
                ・「172.31.0.0/16」のレンジに含まれないこと"
            placement="end"
          />
        </v-col>
      </v-row>
      <!-- SBY-GW対向ルータ(IPv4) -->
      <v-row>
        <v-col>
          <small>GW対向ルータ</small>
          <validation-provider
            label="GW対向ルータ"
            name="sbyWanIpv4Pe"
            v-model="cpaNwSetting.networkIpSettingSby!.wanIpv4Pe"
            :rules="{
              required: true,
              is_ip_subnet: { eq: 30 },
              is_private_address: !cpaNwSetting.isGlobalIpUse,
              is_same_segment: { ip: '@sbyWanIpv4Gw', targetField: 'GWルータ' },
              is_ip_smaller: { ip: '@sbyWanIpv4Gw', targetField: 'GWルータ' },
              is_not_network_ip: true,
              is_not_broadcast_ip: true,
              is_act_sby_not_same: { targetList: actIpv4list, act_sby: 'Act' },
              is_not_inclusion_range: {
                targetList: ipv4list(-1),
              },
              is_not_specific_cpa_wan_address: true,
            }"
            v-slot="{ componentField, errorMessage, meta }"
          >
            <app-form-input
              v-bind="componentField"
              :field-meta="meta"
              :error-messages="errorMessage"
            />
          </validation-provider>
        </v-col>
      </v-row>
      <!-- SBY-GWルータ(IPv4) -->
      <v-row>
        <v-col>
          <small>GWルータ</small>
          <validation-provider
            name="sbyWanIpv4Gw"
            label="GWルータ"
            v-model="cpaNwSetting.networkIpSettingSby!.wanIpv4Gw"
            :rules="{
              required: true,
              is_ip_subnet: { eq: 30 },
              is_private_address: !cpaNwSetting.isGlobalIpUse,
              is_same_segment: {
                ip: '@sbyWanIpv4Pe',
                targetField: 'GW対向ルータ',
              },
              is_ip_greater: {
                ip: '@sbyWanIpv4Pe',
                targetField: 'GW対向ルータ',
              },
              is_not_network_ip: true,
              is_not_broadcast_ip: true,
              is_act_sby_not_same: { targetList: actIpv4list, act_sby: 'Act' },
              is_not_inclusion_range: {
                targetList: ipv4list(-1),
              },
              is_not_specific_cpa_wan_address: true,
            }"
            v-slot="{ componentField, errorMessage, meta }"
          >
            <app-form-input
              v-bind="componentField"
              :field-meta="meta"
              :error-messages="errorMessage"
            />
          </validation-provider>
        </v-col>
      </v-row>
    </template>

    <template v-if="isIpv6">
      <v-row>
        <v-col>
          <app-badge
            item="WAN IP アドレス(IPv6)"
            msg="・IPv6形式(省略不可)(例:2001:0268:f100:0090:0000:0000:0000:0001/64)
             ・サブネットマスクは64固定
             ・GW対向ルータの末尾が「.0001」であること
             ・GWルータの末尾が「.0002」であること
             ・NW設定(Act)のWAN IPアドレス(IPv6)(GW対向ルータ)、WAN IPアドレス(IPv6)(GWルータ)と同じアドレスでないこと
             ・端末割当アドレス(IPv6)で指定したレンジに含まれないこと"
          />
        </v-col>
      </v-row>
      <!-- SBY-GW対向ルータ(IPv6) -->
      <v-row>
        <v-col>
          <small>GW対向ルータ</small>
          <validation-provider
            label="GW対向ルータ"
            name="sbyWanIpv6Pe"
            v-model="cpaNwSetting.networkIpSettingSby!.wanIpv6Pe"
            :rules="{
              required: true,
              is_ipv6_subnet: { eq: 64 },
              is_ipv6_not_omit: true,
              cpa_ipv6_gw_pe: {
                target: cpaNwSetting.networkIpSettingSby!.wanIpv6Gw,
                pe_gw: 'pe',
              },
              is_ipv6_act_sby_not_same: {
                targetList: actIpv6list,
                act_sby: 'Act',
              },
              is_ipv6_not_inclusion_range: { targetList: ipv6list(-1) },
            }"
            v-slot="{ componentField, errorMessage, meta }"
          >
            <app-form-input
              v-bind="componentField"
              :field-meta="meta"
              :error-messages="errorMessage"
            />
          </validation-provider>
        </v-col>
      </v-row>
      <!-- SBY-GWルータ(IPv6) -->
      <v-row>
        <v-col>
          <small>GWルータ</small>
          <validation-provider
            name="sbyWanIpv6Gw"
            label="GWルータ"
            v-model="cpaNwSetting.networkIpSettingSby!.wanIpv6Gw"
            :rules="{
              required: true,
              is_ipv6_subnet: { eq: 64 },
              is_ipv6_not_omit: true,
              cpa_ipv6_gw_pe: {
                target: cpaNwSetting.networkIpSettingSby!.wanIpv6Pe,
                pe_gw: 'gw',
              },
              is_ipv6_act_sby_not_same: {
                targetList: actIpv6list,
                act_sby: 'Act',
              },
              is_ipv6_not_inclusion_range: { targetList: ipv6list(-1) },
            }"
            v-slot="{ componentField, errorMessage, meta }"
          >
            <app-form-input
              v-bind="componentField"
              :field-meta="meta"
              :error-messages="errorMessage"
            />
          </validation-provider>
        </v-col>
      </v-row>
    </template>

    <!-- SBY-DNS IPアドレス(IPv4) -->
    <template v-if="isIpv4">
      <v-row>
        <v-col>
          <app-badge
            item="DNS IPアドレス(IPv4)"
            :is-required="false"
            msg="・IPアドレス/サブネットマスク形式(例:192.168.0.1/32)
              ・サブネットマスクは32固定
              ・グローバルアドレス利用が無の場合、プライベートアドレスのみ指定可"
            placement="end"
          ></app-badge>
          <validation-provider
            label="DNS IPアドレス(IPv4)"
            name="sbyDnsIpv4"
            v-model="cpaNwSetting.networkIpSettingSby!.dnsIpv4"
            :rules="{
              is_ip_subnet: { eq: 32 },
              is_private_address: !cpaNwSetting.isGlobalIpUse,
            }"
            v-slot="{ componentField, errorMessage, meta }"
          >
            <app-form-input
              v-bind="componentField"
              :field-meta="meta"
              :error-messages="errorMessage"
            />
          </validation-provider>
        </v-col>
      </v-row>
    </template>

    <!-- SBY-DNS IPアドレス(IPv6) -->
    <template v-if="isIpv6">
      <v-row>
        <v-col>
          <app-badge
            item="DNS IPアドレス(IPv6)"
            :is-required="false"
            msg="・IPv6形式(省略不可)(例:2001:0268:f100:0090:0000:0000:0000:0001/64)
              ・サブネットマスクは64固定"
          ></app-badge>
          <validation-provider
            name="sbyDnsIpv6"
            label="DNS IPアドレス(IPv6)"
            v-model="cpaNwSetting.networkIpSettingSby!.dnsIpv6"
            :rules="{
              is_ipv6_subnet: { eq: 64 },
              is_ipv6_not_omit: true,
            }"
            v-slot="{ componentField, errorMessage, meta }"
          >
            <app-form-input
              v-bind="componentField"
              :field-meta="meta"
              :error-messages="errorMessage"
            />
          </validation-provider>
        </v-col>
      </v-row>
    </template>

    <!-- アイドルタイマ -->
    <v-row>
      <v-col>
        <app-label-group
          label="アイドルタイマ"
          :value="cpaNwSetting.idleTimer"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script lang="ts">
import type { PropType } from "vue";
import type {
  CpaContract,
  CpaContractOptions,
  CpaNwDetail,
} from "@/apis/Cpa5gSaApi";
import { defineComponent } from "vue";

export default defineComponent({
  name: "SaNwEditStepper1",
  props: {
    /** 親コンポーネントで保持してるCPA NW情報 */
    value: {
      type: Object as PropType<CpaNwDetail>,
      required: true,
    },
    /** CPA契約情報 */
    cpaContract: {
      type: Object as PropType<CpaContract>,
      required: true,
    },
  },
  expose: ["updateFieldArray"],
  data() {
    return {
      isLoaded: false,
      cpaNwSetting: this.value,
      contractBandwidth: {} as CpaContractOptions["contractBandwidth"],
    };
  },
  computed: {
    /** SMF/UPF注意文言 */
    isSmfUpfNote(): boolean {
      return this.cpaContract.addressAssignmentMethod === "DYNAMIC_IP";
    },
    /** IPv4_IPv6 SMF/UPF注意文言間の線表示可否 */
    isDivider(): boolean {
      return this.cpaContract.ipAddressType === "IPV4_IPV6";
    },
    /** 認証IPアドレスの重複チェック用リスト作成 */
    ipv4list() {
      return (index: number) => {
        return this.cpaNwSetting.ueIpAllocationIpv4List?.filter(
          (_, num) => num !== index
        );
      };
    },
    /** 認証IPアドレスV6の重複チェック用リスト作成 */
    ipv6list() {
      return (index: number) => {
        return this.cpaNwSetting.ueIpAllocationIpv6List?.filter(
          (_, num) => num !== index
        );
      };
    },
    /** SBY-GWルータ/GW対向ルータ(IPv4)をリスト作成 */
    sbyIpv4list(): string[] | null {
      if (!this.cpaNwSetting.networkIpSettingSby) return null;
      return [
        this.cpaNwSetting.networkIpSettingSby.wanIpv4Pe || "",
        this.cpaNwSetting.networkIpSettingSby.wanIpv4Gw || "",
      ];
    },
    /** ACT-GWルータ/GW対向ルータ(IPv4)をリスト作成 */
    actIpv4list(): string[] | null {
      if (!this.cpaNwSetting.networkIpSettingAct) return null;
      return [
        this.cpaNwSetting.networkIpSettingAct.wanIpv4Pe || "",
        this.cpaNwSetting.networkIpSettingAct.wanIpv4Gw || "",
      ];
    },
    /** SBY-GWルータ/GW対向ルータ(IPv6)をリスト作成 */
    sbyIpv6list(): string[] | null {
      if (!this.cpaNwSetting.networkIpSettingSby) return null;
      return [
        this.cpaNwSetting.networkIpSettingSby.wanIpv6Pe || "",
        this.cpaNwSetting.networkIpSettingSby.wanIpv6Gw || "",
      ];
    },
    /** ACT-GWルータ/GW対向ルータ(IPv6)をリスト作成 */
    actIpv6list(): string[] | null {
      if (!this.cpaNwSetting.networkIpSettingAct) return null;
      return [
        this.cpaNwSetting.networkIpSettingAct.wanIpv6Pe || "",
        this.cpaNwSetting.networkIpSettingAct.wanIpv6Gw || "",
      ];
    },
    /**
     * 契約帯域プルダウン
     * 契約時に選択された契約種別により表示する内容を変更する
     */
    bandwidthOptions(): {
      value: { value: number; unit: string };
      text: string;
    }[] {
      if (this.cpaContract.bandwidthType === "BEST_EFFORT") return [];
      if (this.cpaContract.contractType === "CPA_NO_LIMIT") {
        // 速度制限無しの場合は帯域の上限以下を選択可能とする
        return [
          { value: { value: 100, unit: "MBPS" }, text: "100Mbps" },
          { value: { value: 200, unit: "MBPS" }, text: "200Mbps" },
          { value: { value: 300, unit: "MBPS" }, text: "300Mbps" },
          { value: { value: 400, unit: "MBPS" }, text: "400Mbps" },
          { value: { value: 500, unit: "MBPS" }, text: "500Mbps" },
          { value: { value: 600, unit: "MBPS" }, text: "600Mbps" },
          { value: { value: 700, unit: "MBPS" }, text: "700Mbps" },
          { value: { value: 800, unit: "MBPS" }, text: "800Mbps" },
          { value: { value: 900, unit: "MBPS" }, text: "900Mbps" },
          { value: { value: 1, unit: "GBPS" }, text: "1Gbps" },
          { value: { value: 2, unit: "GBPS" }, text: "2Gbps" },
          { value: { value: 3, unit: "GBPS" }, text: "3Gbps" },
          { value: { value: 4, unit: "GBPS" }, text: "4Gbps" },
          { value: { value: 5, unit: "GBPS" }, text: "5Gbps" },
          { value: { value: 6, unit: "GBPS" }, text: "6Gbps" },
          { value: { value: 7, unit: "GBPS" }, text: "7Gbps" },
          { value: { value: 8, unit: "GBPS" }, text: "8Gbps" },
          { value: { value: 9, unit: "GBPS" }, text: "9Gbps" },
          { value: { value: 10, unit: "GBPS" }, text: "10Gbps" },
          { value: { value: 20, unit: "GBPS" }, text: "20Gbps" },
          { value: { value: 30, unit: "GBPS" }, text: "30Gbps" },
          { value: { value: 40, unit: "GBPS" }, text: "40Gbps" },
          { value: { value: 50, unit: "GBPS" }, text: "50Gbps" },
          { value: { value: 60, unit: "GBPS" }, text: "60Gbps" },
          { value: { value: 70, unit: "GBPS" }, text: "70Gbps" },
          { value: { value: 80, unit: "GBPS" }, text: "80Gbps" },
          { value: { value: 90, unit: "GBPS" }, text: "90Gbps" },
          { value: { value: 100, unit: "GBPS" }, text: "100Gbps" },
        ].filter((v) => {
          return (
            v.value.unit === "MBPS" ||
            v.value.value <= this.contractBandwidth.value
          );
        });
      } else {
        return [
          { value: { value: 1, unit: "MBPS" }, text: "1Mbps" },
          { value: { value: 10, unit: "MBPS" }, text: "10Mbps" },
          { value: { value: 20, unit: "MBPS" }, text: "20Mbps" },
          { value: { value: 30, unit: "MBPS" }, text: "30Mbps" },
          { value: { value: 40, unit: "MBPS" }, text: "40Mbps" },
          { value: { value: 50, unit: "MBPS" }, text: "50Mbps" },
          { value: { value: 60, unit: "MBPS" }, text: "60Mbps" },
          { value: { value: 70, unit: "MBPS" }, text: "70Mbps" },
          { value: { value: 80, unit: "MBPS" }, text: "80Mbps" },
          { value: { value: 90, unit: "MBPS" }, text: "90Mbps" },
        ];
      }
    },
    /**
     * アドレスタイプがIPv4か判定
     * true: IPv4である false: IPv4ではない
     */
    isIpv4(): boolean {
      return (
        this.cpaContract.ipAddressType === "IPV4" ||
        this.cpaContract.ipAddressType === "IPV4_IPV6"
      );
    },
    /**
     * アドレスタイプがIPv6か判定
     * true: IPv6である false: IPv6ではない
     */
    isIpv6(): boolean {
      return (
        this.cpaContract.ipAddressType === "IPV6" ||
        this.cpaContract.ipAddressType === "IPV4_IPV6"
      );
    },
  },
  methods: {
    /** 端末割当アドレス(IPv4)のフォームを追加する */
    addUeIpAllocationIpv4List(push: (item: any) => void) {
      const addItem = "";
      this.cpaNwSetting.ueIpAllocationIpv4List!.push(addItem);
      // ValidationにFieldを追加することを伝える
      push(addItem);
    },
    /** 端末割当アドレス(IPv4)のフォームを削除する */
    removeUeIpAllocationIpv4List(index: number, remove: (idx: number) => void) {
      if (this.cpaNwSetting.ueIpAllocationIpv4List!.length > 1) {
        this.cpaNwSetting.ueIpAllocationIpv4List!.splice(index, 1);
        // ValidationにFieldを削除することを伝える
        remove(index);
      } else {
        this.cpaNwSetting.ueIpAllocationIpv4List = [""];
      }
    },

    /** 端末割当アドレス(IPv6)のフォームを追加する */
    addUeIpAllocationIpv6List(push: (item: any) => void) {
      const addItem = "";
      this.cpaNwSetting.ueIpAllocationIpv6List!.push(addItem);
      // ValidationにFieldを追加することを伝える
      push(addItem);
    },
    /** 端末割当アドレス(IPv6)のフォームを削除する */
    removeUeIpAllocationIpv6List(index: number, remove: (idx: number) => void) {
      if (this.cpaNwSetting.ueIpAllocationIpv6List!.length > 1) {
        this.cpaNwSetting.ueIpAllocationIpv6List!.splice(index, 1);
        // ValidationにFieldを削除することを伝える
        remove(index);
      } else {
        this.cpaNwSetting.ueIpAllocationIpv6List = [""];
      }
    },
    updateFieldArray(ipFlg: "IPV4" | "IPV6", items: string[]) {
      if (ipFlg === "IPV4") {
        (
          this.$refs.ueIpAllocationIpv4List as {
            replace: (items: unknown[]) => void;
          }
        ).replace([...items]);
      } else {
        (
          this.$refs.ueIpAllocationIpv6List as {
            replace: (items: unknown[]) => void;
          }
        ).replace([...items]);
      }
    },
  },
  watch: {
    /** 親からのForm情報更新を反映 */
    value(newValue: CpaNwDetail) {
      this.cpaNwSetting = newValue;
    },
    /** 親へForm情報更新を通知 */
    cpaNwSetting: {
      handler(newValue: CpaNwDetail) {
        this.$emit("input", newValue);
      },
      deep: true,
    },
  },
  async mounted() {
    // 速度制限無しかつ帯域確保の場合は、帯域値の設定上限を取得する
    if (
      this.cpaContract.contractType === "CPA_NO_LIMIT" &&
      this.cpaContract.bandwidthType === "SECURING"
    ) {
      this.contractBandwidth = (
        await this.$api.cpa5gSa.getCpaContractOptions()
      ).contractBandwidth;
    }
    // 端末割当アドレス(IPv4)が既に設定されている場合は数値順で昇順ソート
    if (
      this.cpaNwSetting.ueIpAllocationIpv4List &&
      this.cpaNwSetting.ueIpAllocationIpv4List.length > 0
    ) {
      this.cpaNwSetting.ueIpAllocationIpv4List =
        this.cpaNwSetting.ueIpAllocationIpv4List.sortIpAddress();
    }
    this.isLoaded = true;
  },
});
</script>
